import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import type { ChoiceFinding } from '@lensmira/domain'

import { Ltr, useTranslation } from '../i18n.js'
import { formatMoneyForDisplay, parseMinorUnits } from '../money.js'
import { DataSurface } from './DataSurface.js'
import { LensComparison } from './LensComparison.js'

/**
 * Choosing a lens for a prescription, with the reasons. §6.30 `DSS-001`, §6.6, `CAT-011`.
 *
 * **This is the half of §6.6 that was missing.** The requirement asks for "one screen, multi-pair,
 * inline warnings", and until now a dispenser typed a product id into `NewOrder` and found out at
 * the server whether the lab could make it. `GET /selection/lenses` has had the answer all along:
 * every makeable lens for this prescription, ranked, with its warnings, plus the ones that are not
 * makeable and the rule that says so.
 *
 * **The unavailable list is the point, not a footnote.** "Why is the lens I always use not in this
 * list" is the question a dispenser asks, and the endpoint answers it per product with
 * `evaluateChoice`'s own findings. Hiding them would leave the list looking arbitrary, which is how
 * a shop ends up ringing the lab to ask.
 *
 * **Nothing here ranks, prices or decides.** The server ordered the list, priced every option
 * against the price book in force, and gated cost and margin at its own response boundary
 * (`ROLE-001a`, a per-branch grant). `rankedBy` says which key it used and `withheld` says what was
 * kept back, and both are shown: a margin ranking a dispenser cannot see the margins behind is an
 * order they cannot account for, and a blank cost column reads as missing data rather than as a
 * permission.
 *
 * **Absent and null are different and stay different.** An absent `costMinor` is "you may not see
 * cost here"; a null one is "no connected supplier quoted this lens". The first is about the
 * person, the second about the lens.
 */

export interface LensOptionData {
  readonly productId: string
  readonly name: string
  readonly priceMinor: string
  /** 1 is the cheapest. Ties share a rank. Never absent: price is not gated. */
  readonly priceRank: number
  /** `warn` findings only. A blocked lens is in `unavailable`, not here. */
  readonly warnings: readonly ChoiceFinding[]
  /** Absent means withheld (`ROLE-001a`); null means no supplier quoted it. */
  readonly costMinor?: string | null
  readonly costSupplierName?: string | null
  readonly marginMinor?: string | null
}

export interface LensSelectionData {
  readonly rxType: string
  readonly currency: string
  readonly rankedBy: 'margin' | 'price'
  /** Which figures the caller may not see, in the server's own words. */
  readonly withheld: readonly string[]
  readonly options: readonly LensOptionData[]
  /** Makeable with no retail price: nothing to rank on, so named rather than dropped. */
  readonly unpriced: readonly {
    readonly productId: string
    readonly name: string
    readonly message: string
  }[]
  /** Not makeable for this prescription, with the rule that refused it. */
  readonly unavailable: readonly {
    readonly productId: string
    readonly name: string
    readonly findings: readonly ChoiceFinding[]
  }[]
}

export interface LensChoiceProps {
  /** Loaded by the caller, which holds the reads config. Stable, or this reloads for ever. */
  readonly load: () => Promise<LensSelectionData>
  /** The product id already on the line, so the list shows which one is chosen. */
  readonly chosenProductId?: string
  readonly onChoose: (choice: { readonly productId: string; readonly name: string }) => void
  /**
   * The non-lens lines already on the order, in minor units, for `DSP-006`'s total. Default `0n`,
   * which is a re-glaze: lenses into the customer's own frame and nothing else on the order.
   */
  readonly otherLinesMinor?: bigint
}

export function LensChoice({
  load,
  chosenProductId,
  onChoose,
  otherLinesMinor = 0n,
}: LensChoiceProps): JSX.Element {
  const { t } = useTranslation()
  const [selection, setSelection] = useState<LensSelectionData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showRefused, setShowRefused] = useState(false)
  /**
   * `DSP-006`: which options are ticked for comparison, and whether the table is open.
   *
   * Capped at three because the requirement says "two or three", and the cap is enforced by
   * disabling the fourth tick rather than by refusing the comparison afterwards: a dispenser who
   * has ticked four and is then told no has done the work twice.
   */
  const [compareIds, setCompareIds] = useState<readonly string[]>([])
  const [comparing, setComparing] = useState(false)

  const toggleCompare = useCallback((productId: string) => {
    setComparing(false)
    setCompareIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : current.length >= 3
          ? current
          : [...current, productId],
    )
  }, [])

  const refresh = useCallback(async () => {
    try {
      setSelection(await load())
      setError(null)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('lensChoice.loadFailed'))
    }
  }, [load, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const amount = (minor: string): string =>
    formatMoneyForDisplay(parseMinorUnits(minor), selection?.currency ?? 'PKR')

  if (error !== null || selection === null) {
    // Nothing else on this component is worth drawing before the read lands, so the surface is the
    // whole component here. A failed read keeps the server's own message, which names the cause
    // (no published price book, say) and is more use than a generic sentence.
    return (
      <DataSurface
        state={
          error !== null
            ? {
                kind: 'error',
                message: error,
                onRetry: () => {
                  setError(null)
                  void refresh()
                },
              }
            : { kind: 'loading' }
        }
        shape="rows"
        rows={4}
        what={t('lensChoice.what')}
      >
        {null}
      </DataSurface>
    )
  }

  return (
    <div className="lm-lens-choice">
      <p className="lm-hint">
        {/* Which key the server ranked by, always, because the order of the list is information. */}
        {selection.rankedBy === 'margin'
          ? t('lensChoice.rankedByMargin')
          : t('lensChoice.rankedByPrice')}
      </p>

      {selection.withheld.length === 0 ? null : (
        <p className="lm-hint">
          {/* `ROLE-001a`: a per-branch grant. Said plainly, so a blank column is not read as a
              gap in the data. The server's own field names, which are not sentences to translate. */}
          {t('lensChoice.withheld')} <Ltr>{selection.withheld.join(', ')}</Ltr>
        </p>
      )}

      {/*
        "Filtered" here means filtered by the prescription. No lens anywhere in the catalogue sends
        the shopkeeper to add one. Lenses that exist but are ruled out sends them to the list of
        reasons below, which is where the answer to "why not mine" is.
      */}
      <DataSurface
        state={
          selection.options.length > 0
            ? { kind: 'ready' }
            : selection.unpriced.length === 0 && selection.unavailable.length === 0
              ? { kind: 'empty' }
              : { kind: 'filtered' }
        }
        shape="rows"
        rows={4}
        what={t('lensChoice.what')}
        emptyMessage={t('lensChoice.noneOnFile')}
        filteredMessage={t('lensChoice.noneMakeable')}
      >
        <ul className="lm-lens-list">
          {selection.options.map((option) => (
            <li
              key={option.productId}
              className="lm-lens-option"
              data-chosen={option.productId === chosenProductId ? 'yes' : undefined}
            >
              <button
                type="button"
                className="lm-button lm-button--secondary lm-lens-option__pick"
                onClick={() => {
                  onChoose({ productId: option.productId, name: option.name })
                }}
              >
                {option.productId === chosenProductId
                  ? t('lensChoice.chosen', { name: option.name })
                  : t('lensChoice.choose', { name: option.name })}
              </button>
              <span className="lm-lens-option__price" dir="ltr">
                {amount(option.priceMinor)}
              </span>
              <label className="lm-lens-option__compare">
                {/* `DSP-006`. The fourth tick is disabled rather than refused later, and the hint
                    says why, so the disabled state is not a dead control with no explanation. */}
                <input
                  type="checkbox"
                  checked={compareIds.includes(option.productId)}
                  disabled={compareIds.length >= 3 && !compareIds.includes(option.productId)}
                  aria-describedby={compareIds.length >= 3 ? 'lens-compare-limit' : undefined}
                  onChange={() => {
                    toggleCompare(option.productId)
                  }}
                />
                <span>{t('lensChoice.compareSelect', { name: option.name })}</span>
              </label>
              {option.costMinor === undefined ? null : (
                <span className="lm-lens-option__cost" dir="ltr">
                  {/* Null is a lens no connected supplier quoted, which is a fact about the lens
                      and not about the reader. */}
                  {option.costMinor === null
                    ? t('lensChoice.costUnquoted')
                    : t('lensChoice.cost', { amount: amount(option.costMinor) })}
                </span>
              )}
              {option.warnings.length === 0 ? null : (
                <ul className="lm-lens-option__warnings">
                  {option.warnings.map((finding, index) => (
                    <li key={`${option.productId}-${finding.code}-${index}`}>
                      {/* `RX-004` makes these advise rather than block, so they sit beside the
                          option and do not stop it being chosen. The server's message, because it
                          names the measurement and the limit. */}
                      {finding.message}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </DataSurface>

      {selection.unpriced.length === 0 ? null : (
        <>
          <h3 className="lm-screen__subheading">{t('lensChoice.unpricedHeading')}</h3>
          <ul className="lm-lens-list">
            {selection.unpriced.map((row) => (
              <li key={row.productId} className="lm-lens-option">
                <span className="lm-lens-option__name">{row.name}</span>
                <span className="lm-lens-option__why">{row.message}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {selection.unavailable.length === 0 ? null : (
        <>
          {/* Behind a disclosure, because a long list of lenses that cannot be made is not what a
              dispenser came for. Open on demand, which is when they ask why one is missing. */}
          <button
            type="button"
            className="lm-button lm-button--secondary"
            aria-expanded={showRefused}
            onClick={() => {
              setShowRefused((open) => !open)
            }}
          >
            {t('lensChoice.whyNot', { count: selection.unavailable.length })}
          </button>
          {showRefused ? (
            <ul className="lm-lens-list">
              {selection.unavailable.map((row) => (
                <li key={row.productId} className="lm-lens-option">
                  <span className="lm-lens-option__name">{row.name}</span>
                  <ul className="lm-lens-option__warnings">
                    {row.findings.map((finding, index) => (
                      <li key={`${row.productId}-${finding.code}-${index}`}>{finding.message}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}

      {/* `DSP-006`. Below the list, because the list is what a dispenser scans first and the
          comparison is the second move, not the entry point. */}
      {selection.options.length < 2 ? null : (
        <>
          {compareIds.length >= 3 ? (
            <p id="lens-compare-limit" className="lm-hint">
              {t('lensChoice.compareLimit')}
            </p>
          ) : null}
          <button
            type="button"
            className="lm-button lm-button--secondary"
            disabled={compareIds.length < 2}
            onClick={() => {
              setComparing(true)
            }}
          >
            {t('lensChoice.compare', { count: compareIds.length })}
          </button>
          {comparing && compareIds.length >= 2 ? (
            <LensComparison
              options={selection.options.filter((o) => compareIds.includes(o.productId))}
              currency={selection.currency}
              otherLinesMinor={otherLinesMinor}
              {...(chosenProductId === undefined ? {} : { chosenProductId })}
              onChoose={onChoose}
            />
          ) : null}
        </>
      )}
    </div>
  )
}
